import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import {
  PushNotifications,
  type Token,
  type PushNotificationSchema,
  type ActionPerformed,
} from '@capacitor/push-notifications';
import { isNative } from './platform';
import { devicesApi, type DevicePlatform } from '../api/endpoints/devices';

type ReceivedHandler = (n: PushNotificationSchema) => void;
type ActionHandler = (a: ActionPerformed) => void;

// State lives at module scope so callers from any layer (AuthContext outside
// the router, a Bridge component inside the router) can interact with the
// same registration without a context provider plumbing through everything.
let currentToken: string | null = null;
let listeners: PluginListenerHandle[] = [];
let onReceived: ReceivedHandler | null = null;
let onAction: ActionHandler | null = null;
let pendingActions: ActionPerformed[] = [];
let registerPromise: Promise<void> | null = null;

// Wired by <PushNotificationsBridge> once react-router is available so
// foreground notifications can show toasts and taps can navigate. Bridge
// mounts AFTER AuthContext starts registration, so any taps that fire before
// the handler is set get buffered and replayed.
export function setPushHandlers(handlers: {
  onReceived?: ReceivedHandler;
  onAction?: ActionHandler;
}): void {
  onReceived = handlers.onReceived ?? null;
  onAction = handlers.onAction ?? null;
  if (onAction && pendingActions.length > 0) {
    const drain = pendingActions;
    pendingActions = [];
    for (const a of drain) onAction(a);
  }
}

export async function registerPushNotifications(): Promise<void> {
  if (!isNative) return;
  if (registerPromise) return registerPromise;

  registerPromise = (async () => {
    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive !== 'granted') return;

    listeners.push(
      await PushNotifications.addListener('registration', async (token: Token) => {
        currentToken = token.value;
        try {
          await devicesApi.register({
            token: token.value,
            platform: Capacitor.getPlatform() as DevicePlatform,
            bundleId: 'com.zeropay.merchantapp',
            locale: navigator.language,
          });
        } catch (err) {
          // Backend registration failure is non-fatal — the device still
          // receives APNs/FCM messages, the server just can't address them.
          console.error('[push] register failed', err);
        }
      }),
    );
    listeners.push(
      await PushNotifications.addListener('registrationError', (err) => {
        console.error('[push] registration error', err);
      }),
    );
    listeners.push(
      await PushNotifications.addListener('pushNotificationReceived', (n) => {
        onReceived?.(n);
      }),
    );
    listeners.push(
      await PushNotifications.addListener('pushNotificationActionPerformed', (a) => {
        if (onAction) onAction(a);
        else pendingActions.push(a);
      }),
    );

    await PushNotifications.register();
  })();

  return registerPromise;
}

export async function unregisterPushNotifications(): Promise<void> {
  if (!isNative) return;
  registerPromise = null;
  pendingActions = [];

  for (const l of listeners) {
    try { await l.remove(); } catch { /* listener already gone */ }
  }
  listeners = [];

  const token = currentToken;
  currentToken = null;
  if (token) {
    try { await devicesApi.unregister(token); } catch { /* server-side cleanup is best-effort */ }
  }
}

export function getCurrentPushToken(): string | null {
  return currentToken;
}
